import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Servicios", key: "servicios", href: "#services" },
  { label: "Concierge+", key: "concierge", href: "#membership" },
  { label: "Experiencias", key: "experiencias", href: "#experiences" },
  { label: "Partners", key: "partners", href: "#partners" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const dashHref = user && user.role === "partner" ? "/partner" : "/dashboard";

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/10 py-4" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <span className="font-serif-display text-2xl tracking-tight text-white">
            Sol<span className="text-gold">Club</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.key}`}
              onClick={() => go(l.href)}
              className="text-xs tracking-luxe-sm uppercase text-white/70 hover:text-white gold-underline transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          {user ? (
            <Link
              to={dashHref}
              data-testid="nav-dashboard-btn"
              className="text-xs tracking-luxe-sm uppercase bg-gold text-black px-6 py-3 hover:bg-[#C5A059] transition-colors"
            >
              Mi Suite
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-btn"
                className="text-xs tracking-luxe-sm uppercase text-white/80 hover:text-gold transition-colors"
              >
                Acceder
              </Link>
              <button
                onClick={() => go("#membership")}
                data-testid="nav-join-btn"
                className="text-xs tracking-luxe-sm uppercase bg-gold text-black px-6 py-3 hover:bg-[#C5A059] transition-colors hover:scale-[1.02]"
              >
                Alta Nuevo Socio
              </button>
            </>
          )}
        </div>

        <button
          className="md:hidden text-white"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden backdrop-blur-xl bg-black/90 border-t border-white/10 mt-4 px-6 py-8 flex flex-col gap-6">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-mobile-${l.key}`}
              className="text-left text-sm tracking-luxe-sm uppercase text-white/80"
            >
              {l.label}
            </button>
          ))}
          <div className="h-px bg-white/10" />
          {user ? (
            <Link to={dashHref} className="text-sm tracking-luxe-sm uppercase text-gold">
              Mi Suite
            </Link>
          ) : (
            <>
              <Link to="/login" className="text-sm tracking-luxe-sm uppercase text-white/80">
                Acceder
              </Link>
              <button
                onClick={() => go("#membership")}
                className="bg-gold text-black px-6 py-3 text-xs tracking-luxe-sm uppercase"
              >
                Alta Nuevo Socio
              </button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
